import { useOrderService } from "../../../cbi-generator/frontend/composables/useOrderFrontendService";
import type { DiscountType } from "../../../cbi-generator/generated/dto/DiscountType";
import type { DonationPurchaseDetails } from "../../../cbi-generator/generated/dto/DonationPurchaseDetails";
import type { MembershipRef } from "../../../cbi-generator/generated/dto/MembershipRef";
import type { LineItemDiscountInstanceRef } from "../../../cbi-generator/generated/dto/LineItemDiscountInstanceRef";
import type { OrderLineDetail } from "../../../cbi-generator/generated/dto/OrderLineDetail";
import type { OrderRef } from "../../../cbi-generator/generated/dto/OrderRef";

export interface ReceiptLineDetails {
  discounts: {
    name: string;
    amountCents: number | null;
    discount?: LineItemDiscountInstanceRef | null;
    discountId?: number | null;
    discountType?: DiscountType | null;
    specifiedCents?: number | null;
    specifiedPercent?: string | null;
  }[];
  taxes: { name: string; amountCents: number | null }[];
  balanceNumber?: string;
  cardNumber?: string;
  donationFund?: string;
  donationInitiative?: string;
  donation?: DonationPurchaseDetails;
  guestMembership?: MembershipRef;
}

export function toReceiptLineDetails(
  details: readonly OrderLineDetail[],
): Record<number, ReceiptLineDetails> {
  return Object.fromEntries(
    details.map((detail) => [
      detail.lineItem.assignId,
      {
        discounts: [...detail.discounts],
        taxes: [...detail.taxes],
        balanceNumber: detail.balanceNumber ?? undefined,
        cardNumber: detail.cardNumber ?? undefined,
        donationFund: detail.donationFund ?? undefined,
        donationInitiative: detail.donationInitiative ?? undefined,
        donation: detail.donation ?? undefined,
        guestMembership: detail.guestMembership ?? undefined,
      } satisfies ReceiptLineDetails,
    ]),
  );
}

/** Per-line purchase numbers and named adjustments, labelled by the backend like the printed receipt. */
export async function loadReceiptLineDetails(
  order: OrderRef,
  mode: "Staff" | "Member",
): Promise<Record<number, ReceiptLineDetails>> {
  const service = useOrderService();
  return toReceiptLineDetails(
    mode === "Staff"
      ? await service.orderLineDetailsStaff(order)
      : await service.orderLineDetailsMember(order),
  );
}
